/**
 * Browser performance / auth telemetry helpers (New Relic page actions only).
 * Never pass emails, passwords, or tokens into these helpers.
 */

type Nreum = {
  addPageAction?: (name: string, attrs: Record<string, unknown>) => void;
};

export type AuthPageOutcome =
  | "success"
  | "failure"
  | "requires_2fa"
  | "not_found"
  | "denied";

export type LocationMapMilestoneName =
  | "page_open"
  | "tree_data_ready"
  | "navigation_started"
  | "detail_data_ready"
  | "detail_failed"
  | "ws_connected"
  | "ws_first_message"
  | "ws_failed"
  | "2d_first_frame"
  | "3d_first_frame"
  | "navigation_failed";

type LocationMapCorrelation = {
  sessionId: string;
  navId: string | null;
  navStartedAt: number | null;
};

let locationMapCorrelation: LocationMapCorrelation = {
  sessionId: "",
  navId: null,
  navStartedAt: null,
};

function getNreum(): Nreum | undefined {
  return (window as unknown as { NREUM?: Nreum }).NREUM;
}

/** New Relic's SPA agent can throw (reportAllChanges / startTime). Never let that break page data. */
export function nreumPageAction(name: string, attrs: Record<string, unknown>): void {
  try {
    getNreum()?.addPageAction?.(name, attrs);
  } catch {
    /* agent bugs must not block Apollo / React */
  }
}

export function createRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function startTimer(): () => number {
  const start = performance.now();
  return () => Math.round(performance.now() - start);
}

export function recordAuthPageAction(attrs: {
  action: string;
  outcome: AuthPageOutcome;
  duration_ms: number;
  requestId?: string;
  failure_stage?: string;
  role?: string;
  requires_2fa?: boolean;
  cache_hit?: boolean;
  queue_depth?: number;
  status?: number;
}): void {
  nreumPageAction("AuthActionCompleted", {
    action: attrs.action,
    outcome: attrs.outcome,
    duration_ms: attrs.duration_ms,
    requestId: attrs.requestId,
    failure_stage: attrs.failure_stage,
    role: attrs.role,
    requires_2fa: attrs.requires_2fa,
    cache_hit: attrs.cache_hit,
    queue_depth: attrs.queue_depth,
    status: attrs.status,
  });
}

/** Headers to correlate a browser auth journey with GraphQL / auth-service. */
export function authCorrelationHeaders(requestId?: string): Record<string, string> {
  return {
    "x-request-id": requestId || createRequestId(),
  };
}

export function initLocationMapSession(): string {
  const sessionId = createRequestId();
  locationMapCorrelation = {
    sessionId,
    navId: null,
    navStartedAt: performance.now(),
  };
  return sessionId;
}

export function beginLocationMapNavigation(): string {
  const navId = createRequestId();
  locationMapCorrelation = {
    ...locationMapCorrelation,
    sessionId: locationMapCorrelation.sessionId || createRequestId(),
    navId,
    navStartedAt: performance.now(),
  };
  return navId;
}

export function getLocationMapCorrelation(): LocationMapCorrelation {
  return { ...locationMapCorrelation };
}

export function locationMapCorrelationHeaders(): Record<string, string> {
  const { sessionId, navId } = locationMapCorrelation;
  const headers: Record<string, string> = {};
  if (sessionId) headers["x-location-map-session-id"] = sessionId;
  if (navId) headers["x-location-map-nav-id"] = navId;
  return headers;
}

export function recordLocationMapMilestone(
  milestone: LocationMapMilestoneName,
  extra?: Record<string, unknown>
): void {
  const { sessionId, navId, navStartedAt } = locationMapCorrelation;
  const duration_ms =
    typeof navStartedAt === "number" ? Math.round(performance.now() - navStartedAt) : undefined;
  nreumPageAction("LocationMapMilestone", {
    milestone,
    sessionId: sessionId || undefined,
    navId: navId || undefined,
    duration_ms,
    ...(extra || {}),
  });
}

/**
 * Browser console GraphQL/perf logs for local, company-dev, and company-stg hosts.
 * Staging builds set NODE_ENV=production, so do not rely on that flag.
 */
export function shouldLogPerfToConsole(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".localhost") ||
    host.startsWith("192.168.") ||
    host.startsWith("10.")
  ) {
    return true;
  }
  if (host.includes("-dev.") || host.includes(".dev.") || host.includes("dev.")) {
    return true;
  }
  if (host.includes("-stg.") || host.includes(".stg.") || host.includes("stg.")) {
    return true;
  }
  return false;
}
